import { useEffect, useCallback, useReducer } from 'react';

/** When one of these input types is receiving the key event, ignore it */
const EXCLUDED_EVENT_TARGETS = ['INPUT', 'TEXTAREA'];

const SET_KEY_DOWN = 'set-key-down';
const SET_KEY_UP = 'set-key-up';

type KeyboardState = Record<string, boolean>;
type KeyDownAction = {
  type: typeof SET_KEY_DOWN;
  key: string;
};

type KeyUpAction = {
  type: typeof SET_KEY_UP;
  key: string;
};

type KeyboardAction = KeyDownAction | KeyUpAction;

const keysReducer = (state: KeyboardState, action: KeyboardAction): KeyboardState => {
  const watchedKeys = Object.keys(state).map((k) => k.toLowerCase());

  if (watchedKeys.includes(action.key)) {
    switch (action?.type) {
      case 'set-key-down':
        return { ...state, [action.key]: true };
      case 'set-key-up':
        return { ...state, [action.key]: false };
      default:
        return state;
    }
  }
  return state;
};

/**
 * Custom hook for invoking a callback when a certain keyboard sequence is pressed.
 *
 * @example
 * // When control-shift-a are pressed at the same time, add awesome sauce:
 * useKeyboardShortcut(['Control', 'Shift', 'A'], () => alert('Awesome sauce added')));
 */
export const useKeyboardShortcut = (shortcutKeys: string[], callback: () => void): void => {
  if (!Array.isArray(shortcutKeys))
    throw new Error(
      'The first parameter to `useKeyboardShortcut` must be an ordered array of `KeyboardEvent.key` strings.'
    );

  if (!shortcutKeys.length)
    throw new Error(
      'The first parameter to `useKeyboardShortcut` must contain atleast one `KeyboardEvent.key` string.'
    );

  if (!callback || typeof callback !== 'function')
    throw new Error(
      'The second parameter to `useKeyboardShortcut` must be a function that will be envoked when the keys are pressed.'
    );

  const initalKeyMapping: KeyboardState = shortcutKeys.reduce((currentKeys, key) => {
    const loweredKey = typeof key === 'string' ? key.toLowerCase() : key;
    return {
      ...currentKeys,
      [loweredKey]: false,
    };
  }, {} as KeyboardState);

  const [keys, setKeys] = useReducer(keysReducer, initalKeyMapping);

  const keyListener = useCallback(
    (type: typeof SET_KEY_UP | typeof SET_KEY_DOWN, keyboardEvent: KeyboardEvent) => {
      const { key, target, repeat } = keyboardEvent;
      const loweredKey = typeof key === 'string' ? key.toLowerCase() : key;

      if (repeat && type === SET_KEY_DOWN) return;
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore: This is frustrating, you either get a TS error here or in the event listeners if you use React.KeyboardEvent
      if (EXCLUDED_EVENT_TARGETS.includes(target?.tagName)) return;
      setKeys({ type, key: loweredKey });
    },
    []
  );

  useEffect(() => {
    if (Object.keys(keys).every((k) => keys[k])) {
      callback();
    }
  }, [callback, keys]);

  useEffect(() => {
    const downListener = (event: KeyboardEvent) => keyListener(SET_KEY_DOWN, event);
    window.addEventListener('keydown', downListener, true);
    return () => window.removeEventListener('keydown', downListener, true);
  }, [keyListener]);

  useEffect(() => {
    const upListener = (event: KeyboardEvent) => keyListener(SET_KEY_UP, event);
    window.addEventListener('keyup', upListener, true);
    return () => window.removeEventListener('keyup', upListener, true);
  }, [keyListener]);
};

export default useKeyboardShortcut;
